import { isInternalMemberRole } from "@/domain/organization";
import { useEscapeCascade } from "@/presentation/hooks/useEscapeCascade";
import { useCommentsPanel } from "@/presentation/hooks/useCommentsPanel";
import { ShellCommentsPanel } from "./ShellCommentsPanel";
import { ShellCommentsTrigger } from "./ShellCommentsTrigger";
import { ShellCommentsContext } from "@/presentation/hooks/commentsContext";
import { Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, isSidebarCollapsed, setSidebarCollapsed } from "@hulahoop/ui";
import type { useOrganizations } from "@/application/workflows/organization";
import { useShellShortcuts } from "@/presentation/hooks/useShellShortcuts";
import { useOrganizationStore } from "@/stores/useOrganizationStore";
import { SiblingRecordMenu } from "@/components/shared/entity/SiblingRecordMenu";
import { ShellBoundary } from "./ShellBoundary";
import { ShellTopBar } from "./ShellTopBar";
import { CreateInvestmentHost } from "./CreateInvestmentHost";
import { ShellCommandPalette } from "./ShellCommandPalette";
import { ShellCreateModals } from "./ShellCreateModals";
import { ShellHelpDialog } from "./ShellHelpDialog";
import { ShellInbox } from "./ShellInbox";
import { ShellSidebar } from "./ShellSidebar";
import { SHELL_CONTENT_ID, getShellCollectionPath, getShellPageLabel } from "./shellNavigation";
import { useShellInbox } from "./useShellInbox";
import { useShellOrganizationSwitch } from "./useShellOrganizationSwitch";
import type { ShellOutlet, ShellRecordCrumbs } from "./useShellRecord";
import { useShellSignOut } from "./useShellSignOut";

interface AppShellProps {
  organizations: ReturnType<typeof useOrganizations>;
  loadingFallback: ReactNode;
  /** The record the page is showing; the collection crumb becomes a link. */
  record?: string;
  /** Intermediate crumbs between the collection and the record. */
  crumbs?: { label: string; href: string }[];
  /** The sibling-record switcher anchored under the record name. */
  recordMenu?: ReactNode;
  /** Record state next to its name (a lock, «Saving… → Saved»). */
  recordExtra?: ReactNode;
  /** Page controls injected into the chrome. */
  actions?: ReactNode;
  /** Starts the sidebar in rail while the page is mounted, without persisting. */
  railSidebar?: boolean;
}

export function AppShell({
  organizations,
  loadingFallback,
  record,
  crumbs,
  recordMenu,
  recordExtra,
  actions,
  railSidebar,
}: AppShellProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const selectedUuid = useOrganizationStore((state) => state.selectedOrganizationUuid);
  const inboxRef = useRef<HTMLElement>(null);
  const panels = useShellShortcuts(inboxRef);
  const inbox = useShellInbox();
  const signOut = useShellSignOut();
  const handleSwitch = useShellOrganizationSwitch(organizations);
  const switching = organizations.isSwitching;
  const commentsEnabled =
    !pathname.startsWith("/settings") &&
    isInternalMemberRole(organizations.currentOrganization?.user_role.toLowerCase() ?? "");
  const comments = useCommentsPanel(commentsEnabled);
  useEscapeCascade([]);
  const commentsPanel =
    commentsEnabled && comments.state.open ? <ShellCommentsPanel comments={comments} /> : undefined;
  const pageLabel = getShellPageLabel(pathname);
  const collection = pageLabel ? t(pageLabel) : "";
  const [pageRecord, setPageRecord] = useState<ShellRecordCrumbs | null>(null);
  const [recordExtraSlot, setRecordExtraSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!railSidebar) return;
    const previous = isSidebarCollapsed();
    setSidebarCollapsed(true, { persist: false });
    return () => setSidebarCollapsed(previous, { persist: false });
  }, [railSidebar]);
  return (
    <div data-sidebar-collapsible className="flex h-dvh bg-sidebar">
      <a href={`#${SHELL_CONTENT_ID}`} className="sr-only focus:not-sr-only">
        {t("shell.skip_navigation")}
      </a>
      <ShellSidebar
        organizationName={
          organizations.currentOrganization?.organization_name ?? t("nav.organization")
        }
        organizationLogoUrl={organizations.currentOrganization?.organization_logo_url ?? null}
        organizations={organizations.userOrganizations}
        selectedUuid={selectedUuid}
        switching={switching || organizations.isLoadingOrganizations}
        inboxOpen={panels.open === "inbox"}
        unreadCount={inbox.unreadCount}
        onSwitch={(uuid) => {
          panels.closePanel("inbox");
          void handleSwitch(uuid);
        }}
        onSearch={() => panels.openPanel("command")}
        onInbox={() => panels.togglePanel("inbox")}
        onHelp={() => panels.openPanel("help")}
      />
      <main id={SHELL_CONTENT_ID} tabIndex={-1} className="relative flex min-w-0 flex-1 flex-col">
        <ShellTopBar
          collection={collection}
          collectionHref={getShellCollectionPath(pathname)}
          record={record ?? pageRecord?.record}
          crumbs={crumbs ?? pageRecord?.crumbs}
          recordMenu={
            recordMenu ??
            (pageRecord?.siblings ? (
              <SiblingRecordMenu
                items={pageRecord.siblings.items}
                current={pageRecord.siblings.current}
              />
            ) : undefined)
          }
          recordExtra={
            <>
              {recordExtra}
              <span className="contents" ref={setRecordExtraSlot} />
            </>
          }
          actions={
            <>
              {actions}
              {commentsEnabled && (
                <ShellCommentsTrigger
                  open={comments.state.open}
                  unread={!!comments.summary.data?.unread_count}
                  label={comments.labels.title}
                  unreadLabel={comments.labels.unread}
                  onToggle={() =>
                    comments.state.open ? comments.close() : comments.state.setOpen(true)
                  }
                />
              )}
            </>
          }
          onSignOut={signOut.request}
        />
        {organizations.error ? (
          <div role="alert" className="flex flex-col items-start gap-2 px-2">
            <p>{t("shell.organizations_failed")}</p>
            <Button onClick={organizations.retryUserOrganizations}>{t("shell.retry")}</Button>
          </div>
        ) : switching || !selectedUuid ? (
          loadingFallback
        ) : (
          <Suspense fallback={loadingFallback}>
            <ShellBoundary
              key={pathname}
              message={t("shell.page_failed")}
              className="flex flex-col gap-2 px-2"
            >
              <ShellCommentsContext.Provider value={commentsPanel}>
                <Outlet
                  context={{ setRecord: setPageRecord, recordExtraSlot } satisfies ShellOutlet}
                />
              </ShellCommentsContext.Provider>
            </ShellBoundary>
          </Suspense>
        )}
        {panels.open === "inbox" && !switching && (
          <ShellBoundary
            message={t("shell.widget_failed")}
            className="flex flex-col gap-2 px-2"
            action={
              <Button variant="ghost" onClick={() => panels.closePanel("inbox")}>
                {t("notifications.close_inbox")}
              </Button>
            }
          >
            <ShellInbox ref={inboxRef} inbox={inbox} onClose={() => panels.closePanel("inbox")} />
          </ShellBoundary>
        )}
        <ShellCommandPalette
          open={panels.open === "command"}
          onOpenChange={(open) =>
            open ? panels.openPanel("command") : panels.closePanel("command")
          }
          onSignOut={signOut.request}
        />
        <ShellHelpDialog
          open={panels.open === "help"}
          onOpenChange={(open) => (open ? panels.openPanel("help") : panels.closePanel("help"))}
        />
        <CreateInvestmentHost />
        <ShellCreateModals />
        {signOut.dialog}
      </main>
    </div>
  );
}
